import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, FileSpreadsheet, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { useApi, openBlob, errMsg } from "../lib/api";
import { PageHeader, DataState, AvailBadge, Avatar, NativeSelect } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { WorkerForm } from "../components/Forms";
import { eur2 } from "../lib/format";

function WorkersFilters({ f, set, qualifications }) {
  return (
    <div className="surface p-3 mb-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-[1fr_200px_200px_220px]">
      <div className="relative"><Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
        <Input className="pl-9 h-10" placeholder="Cerca nome, codice fiscale, qualifica…" value={f.q} onChange={(e) => set("q", e.target.value)} data-testid="workers-search-input" /></div>
      <NativeSelect value={f.qualification} onChange={(e) => set("qualification", e.target.value)} data-testid="workers-qualification-filter">
        <option value="">Tutte le mansioni</option>{qualifications?.map((x) => <option key={x}>{x}</option>)}
      </NativeSelect>
      <NativeSelect value={f.status} onChange={(e) => set("status", e.target.value)} data-testid="workers-status-filter">
        <option value="">Tutti gli stati</option><option value="disponibile">🟢 Disponibile</option><option value="impegnato">🔴 Impegnato</option><option value="assente">🟠 Assente</option>
      </NativeSelect>
      <NativeSelect value={f.sort} onChange={(e) => set("sort", e.target.value)} data-testid="workers-sort-select">
        <option value="last_name">Ordina: Cognome A-Z</option><option value="last_name:desc">Cognome Z-A</option>
        <option value="first_name">Nome A-Z</option><option value="first_name:desc">Nome Z-A</option>
        <option value="qualification">Qualifica</option><option value="hourly_company_cost:desc">Costo orario ↓</option><option value="hire_date">Data assunzione</option>
      </NativeSelect>
    </div>
  );
}

function WorkerRow({ w, onOpen }) {
  return (
    <tr onClick={() => onOpen(w.id)} className="border-b border-border/50 hover:bg-accent/40 cursor-pointer" data-testid={`worker-row-${w.id}`}>
      <td className="p-3"><div className="flex items-center gap-3"><Avatar name={w.full_name} photo={w.photo} size="h-9 w-9" /><div><div className="font-semibold">{w.last_name} {w.first_name}</div><div className="text-xs text-muted-foreground font-mono">{w.fiscal_code}</div></div></div></td>
      <td className="p-3">{w.qualification}</td>
      <td className="p-3 num text-xs">{w.phone}</td>
      <td className="p-3 text-right num">{eur2(w.hourly_company_cost)}</td>
      <td className="p-3"><AvailBadge status={w.availability.status} testid={`worker-status-${w.id}`} /></td>
      <td className="p-3 text-muted-foreground">{w.availability.site_name || "—"}</td>
      <td className="p-3"><ChevronRight className="h-4 w-4 text-muted-foreground" /></td>
    </tr>
  );
}

function WorkersTable({ items, onOpen }) {
  return (
    <div className="surface hidden md:block overflow-x-auto">
      <table className="w-full text-sm" data-testid="workers-table">
        <thead><tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
          <th className="p-3">Operaio</th><th className="p-3">Qualifica</th><th className="p-3">Cellulare</th><th className="p-3 text-right">Costo az./h</th><th className="p-3">Stato</th><th className="p-3">Cantiere oggi</th><th /></tr></thead>
        <tbody>{items.map((w) => <WorkerRow key={w.id} w={w} onOpen={onOpen} />)}</tbody>
      </table>
    </div>
  );
}

function WorkerCards({ items, onOpen }) {
  return (
    <div className="md:hidden space-y-3">
      {items.map((w) => (
        <button key={w.id} onClick={() => onOpen(w.id)} className="surface w-full p-4 text-left flex items-center gap-3" data-testid={`worker-card-${w.id}`}>
          <Avatar name={w.full_name} photo={w.photo} size="h-12 w-12" />
          <div className="flex-1 min-w-0"><div className="font-semibold">{w.full_name}</div><div className="text-xs text-muted-foreground">{w.qualification} · {w.availability.site_name || "—"}</div><div className="mt-2"><AvailBadge status={w.availability.status} /></div></div>
          <ChevronRight className="h-5 w-5 text-muted-foreground" />
        </button>
      ))}
    </div>
  );
}

export default function Workers() {
  const nav = useNavigate();
  const [params, setParams] = useState({ q: "", qualification: "", status: "", sort: "last_name" });
  const [open, setOpen] = useState(false);
  const { data, loading, reload } = useApi("/workers", params);
  const setParam = (k, v) => setParams((p) => ({ ...p, [k]: v }));
  const openWorker = (id) => nav(`/operai/${id}`);
  const items = data?.items || [];

  const exportXlsx = () =>
    openBlob(`/workers/export?${new URLSearchParams(params)}`, "operai.xlsx")
      .then(() => toast.success("✓ Esportazione Excel completata"))
      .catch((e) => toast.error(errMsg(e)));

  return (
    <div>
      <PageHeader eyebrow="Anagrafica" title="Operai" subtitle={`${data?.items?.length ?? "…"} operai registrati`}
        actions={<>
          <Button variant="outline" onClick={exportXlsx} data-testid="workers-export-btn"><FileSpreadsheet className="h-4 w-4 mr-2" />Esporta Excel</Button>
          <Button onClick={() => setOpen(true)} data-testid="workers-new-btn"><Plus className="h-4 w-4 mr-2" />Nuovo operaio</Button>
        </>} />
      <WorkersFilters f={params} set={setParam} qualifications={data?.qualifications} />
      <DataState loading={loading && !data} empty={!items.length} emptyTitle="Nessun operaio trovato">
        <>
          <WorkersTable items={items} onOpen={openWorker} />
          <WorkerCards items={items} onOpen={openWorker} />
        </>
      </DataState>
      <WorkerForm open={open} onOpenChange={setOpen} onSaved={reload} />
    </div>
  );
}
